import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete'; // Using Autocomplete for search functionality
import TextField from '@mui/material/TextField'; // For the search box in Autocomplete
import { useState } from 'react';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';


/**
 * A wrapper around Select to use in a DataGrid column.
 * Will convert the value to the corresponding option value
 * and vice versa.
 * @param {string} value The current value of the cell
 * @param {function} onChange The function to call when the value changes
 * @param {array} options The options to display in the dropdown
 * @param {function} getOptionKey The function to get the key from an option
 * @param {function} getOptionValue The function to get the value from an option
 * @param {function} getOptionLabel The function to get the label from an option
 * @param {string} label The label to display in the dropdown
 * @param {object} sx The sx object to apply to the FormSelect
 * @returns {React.Component} The component to render in the DataGrid cell
 */
export default function FormSelect({
  value,
  onChange,
  options,
  getOptionKey,
  getOptionValue,
  getOptionLabel,
  label,
  sx
}) {
  return (
    <FormControl variant="outlined" sx={sx}>
      <InputLabel>{label}</InputLabel>

      <Select
        value={value}
        onChange={onChange}
        label={label}
        sx={selectSX}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          PaperProps: {
            style: {
              maxHeight: 600,
              overflow: 'auto',
            },
          },
        }}
      >
        {options.map(item => (
          <MenuItem
            key={getOptionKey(item)}
            value={getOptionValue(item)}
          >
            {getOptionLabel(item)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/**
 * A wrapper around Autocomplete to use in a DataGrid column.
 * Will convert the value to the corresponding option value
 * and vice versa.
 * @param {string} value The current value of the cell
 * @param {function} onChange The function to call when the value changes
 * @param {array} options The options to display in the dropdown
 * @param {function} getOptionKey The function to get the key from an option
 * @param {function} getOptionValue The function to get the value from an option
 * @param {function} getOptionLabel The function to get the label from an option
 * @param {string} label The label to display in the dropdown
 * @param {object} sx The sx object to apply to the Autocomplete
 * @returns {React.Component} The component to render in the DataGrid cell
 */
export function ColumnFormSelect({
  value,
  onChange,
  options,
  getOptionKey,
  getOptionValue,
  getOptionLabel,
  label,
  sx
}) {
  return (
    <Autocomplete
      sx={{...sx, ...selectSX}}
      options={options}
      getOptionLabel={(option) => {
        if (!option) {
          return '';
        }
        return getOptionLabel(option);
      }}
      isOptionEqualToValue={(option, val) => {
        if (!val) {
          return false;
        }
        return getOptionKey(option) === getOptionKey(val);
      }}
      onChange={(event, newValue) => {
        const returnedValue = newValue ? getOptionValue(newValue) : null;
        onChange({
          target: {
            value: returnedValue,
          },
        });
      }}
      value={
        options.find(option => getOptionValue(option) === value) || null
      }
      disableClearable
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
        />
      )}
    />
  );
}


export const outlinedInputSX = {
  '& .MuiInputLabel-root': {
    color: 'var(--headerfooter-bg-color)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'var(--headerfooter-bg-color)',
  },
  '& .MuiOutlinedInput-input': {
    color: 'black',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--headerfooter-bg-color)',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    border: '2px solid var(--headerfooter-bg-color)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--headerfooter-bg-color)',
  },
};

export const selectSX = {
  height: 50,
  color: 'var(--headerfooter-bg-color)',
  '& .MuiSelect-select': {
    color: 'black',
  },
  '& .MuiSelect-icon': {
    color: 'var(--headerfooter-bg-color)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--main-hover-color)',
    border: 2,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--headerfooter-bg-color)',
  },
  ...outlinedInputSX,
};

